import { useEffect, useState } from 'react';
import {
  listCampaigns,
  listQueuedForMe,
  type Campaign,
  type QueuedForMeRow,
} from '../api/campaigns';
import { isDialApiConfigured } from '../api/client';
import { CampaignCreate } from './CampaignCreate';
import { AssignDialog } from './AssignDialog';

export function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [queued, setQueued] = useState<QueuedForMeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState<Campaign | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, mine] = await Promise.all([listCampaigns(), listQueuedForMe()]);
      setCampaigns(list);
      setQueued(mine);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  if (creating) {
    return (
      <CampaignCreate
        onCreated={() => {
          setCreating(false);
          void refresh();
        }}
        onCancel={() => setCreating(false)}
      />
    );
  }

  return (
    <main className="px-8 py-10">
      <div className="mx-auto max-w-3xl">
        <header className="flex items-baseline justify-between">
          <div>
            <h1 className="font-sans text-3xl font-semibold tracking-tight text-graphite-900">
              Campaigns
            </h1>
            <p className="mt-2 font-sans text-sm leading-relaxed text-graphite-700">
              Anyone in DeltaDial can build one and push it to the team.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="rounded-md bg-signal-amber px-4 py-2 font-sans text-sm font-medium text-surface hover:bg-signal-amber-hover"
          >
            New campaign
          </button>
        </header>

        {!isDialApiConfigured ? (
          <p className="mt-6 rounded-md border border-dashed border-graphite-300 bg-surface-sunk px-4 py-2 font-mono text-xs text-graphite-500">
            Worker not reachable — campaigns won't persist until the worker is wired.
          </p>
        ) : null}

        {queued.length > 0 ? (
          <section className="mt-8 rounded-lg border border-signal-amber/40 bg-signal-amber/5 p-5">
            <h2 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-signal-amber">
              Queued for me
            </h2>
            <ul className="mt-3 divide-y divide-graphite-100">
              {queued.map((row) => (
                <li
                  key={row.campaign_id}
                  className="flex items-baseline justify-between gap-3 py-2"
                >
                  <span className="font-sans text-sm text-graphite-900">{row.campaigns.name}</span>
                  <span className="font-mono text-xs text-graphite-500">
                    {row.campaigns.point_goal ? `${row.campaigns.point_goal}pt goal · ` : ''}
                    assigned {new Date(row.assigned_at).toLocaleDateString('en-AU')}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="mt-8">
          <h2 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
            All campaigns
          </h2>
          {loading ? (
            <p className="mt-3 font-mono text-xs text-graphite-500">loading…</p>
          ) : campaigns.length === 0 ? (
            <p className="mt-3 rounded-md border border-dashed border-graphite-300 bg-surface-sunk px-4 py-6 text-center font-mono text-xs text-graphite-500">
              No campaigns yet. Click <span className="text-graphite-900">New campaign</span> to
              create one.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-graphite-100 rounded-lg border border-graphite-100 bg-surface">
              {campaigns.map((c) => (
                <li
                  key={c.id}
                  className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-baseline sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="font-sans text-sm text-graphite-900">{c.name}</p>
                    <p className="truncate font-mono text-[0.7rem] text-graphite-500">
                      {[
                        c.starts_at && `from ${c.starts_at}`,
                        c.ends_at && `to ${c.ends_at}`,
                        c.point_goal && `goal ${c.point_goal}pt`,
                        c.is_mine && 'mine',
                        c.is_assigned_to_me && 'assigned',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-baseline gap-3 font-mono text-xs">
                    <span
                      className={c.status === 'active' ? 'text-signal-amber' : 'text-graphite-500'}
                    >
                      {c.status}
                    </span>
                    <button
                      type="button"
                      onClick={() => setAssigning(c)}
                      className="rounded-md border border-graphite-300 px-2 py-1 text-graphite-700 hover:bg-surface-sunk"
                    >
                      Push to team
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {error ? <p className="mt-6 font-mono text-xs text-signal-amber">{error}</p> : null}
      </div>

      {assigning ? (
        <AssignDialog
          campaign={assigning}
          onClose={() => setAssigning(null)}
          onAssigned={() => {
            setAssigning(null);
            void refresh();
          }}
        />
      ) : null}
    </main>
  );
}
